import { EPSILON, SPATIAL_CELL_SIZE_MM } from './constants.js';
import type { BrickId, PlacedBrick } from './brick.js';
import type { Mm } from './units.js';

type CellKey = string;

const cellKey = (cx: number, cy: number): CellKey => `${cx},${cy}`;

/**
 * SPECIFICATION.md clause 5.2 — a uniform hash grid of 500 mm cells, slightly
 * larger than a block, so local collision, snapping, hit-testing and viewport
 * queries stay small even on very large walls.
 *
 * Query results are always returned in INSERTION ORDER, which is what makes
 * hit-testing ("topmost is last inserted") and the perpend solver
 * deterministic.
 */
export class SpatialIndex {
  #byId = new Map<BrickId, PlacedBrick>();
  #orderById = new Map<BrickId, number>();
  #cells = new Map<CellKey, Set<BrickId>>();
  #cellsByBrick = new Map<BrickId, readonly CellKey[]>();
  #nextOrder = 0;

  get size(): number {
    return this.#byId.size;
  }

  get occupiedCellCount(): number {
    return this.#cells.size;
  }

  has(id: BrickId): boolean {
    return this.#byId.has(id);
  }

  get(id: BrickId): PlacedBrick | undefined {
    return this.#byId.get(id);
  }

  /** Insertion rank; later means "on top" for hit-testing. */
  order(id: BrickId): number {
    return this.#orderById.get(id) ?? -1;
  }

  clear(): void {
    this.#byId.clear();
    this.#orderById.clear();
    this.#cells.clear();
    this.#cellsByBrick.clear();
    this.#nextOrder = 0;
  }

  add(brick: PlacedBrick): void {
    const keys = this.#keysForRect(brick.x, brick.y, brick.right, brick.top);
    this.#byId.set(brick.id, brick);
    this.#orderById.set(brick.id, this.#nextOrder++);
    this.#cellsByBrick.set(brick.id, keys);
    for (const key of keys) {
      let bucket = this.#cells.get(key);
      if (bucket === undefined) {
        bucket = new Set();
        this.#cells.set(key, bucket);
      }
      bucket.add(brick.id);
    }
  }

  remove(id: BrickId): void {
    for (const key of this.#cellsByBrick.get(id) ?? []) {
      const bucket = this.#cells.get(key);
      if (bucket === undefined) continue;
      bucket.delete(id);
      if (bucket.size === 0) this.#cells.delete(key);
    }
    this.#cellsByBrick.delete(id);
    this.#byId.delete(id);
    this.#orderById.delete(id);
  }

  #keysForRect(left: Mm, bottom: Mm, right: Mm, top: Mm): readonly CellKey[] {
    const minX = Math.floor(left / SPATIAL_CELL_SIZE_MM);
    const maxX = Math.floor(right / SPATIAL_CELL_SIZE_MM);
    const minY = Math.floor(bottom / SPATIAL_CELL_SIZE_MM);
    const maxY = Math.floor(top / SPATIAL_CELL_SIZE_MM);
    const keys: CellKey[] = [];
    for (let cy = minY; cy <= maxY; cy++) {
      for (let cx = minX; cx <= maxX; cx++) keys.push(cellKey(cx, cy));
    }
    return keys;
  }

  /**
   * Bricks whose axis-aligned box touches the rectangle, in insertion order.
   * Corners may be given in any order.
   */
  intersecting(x1: Mm, y1: Mm, x2: Mm, y2: Mm): PlacedBrick[] {
    const hits = this.intersectingUnordered(x1, y1, x2, y2);
    hits.sort((a, b) => this.order(a.id) - this.order(b.id));
    return hits;
  }

  /**
   * The same query without the ordering pass.
   *
   * Insertion order matters for hit-testing and for the perpend solver's
   * determinism, but a clearance check only asks "does anything clash", and
   * over a dense wall that sort is the dominant cost of a paste preview.
   */
  intersectingUnordered(x1: Mm, y1: Mm, x2: Mm, y2: Mm): PlacedBrick[] {
    if (this.#byId.size === 0) return [];
    const left = Math.min(x1, x2);
    const right = Math.max(x1, x2);
    const bottom = Math.min(y1, y2);
    const top = Math.max(y1, y2);

    const minX = Math.floor(left / SPATIAL_CELL_SIZE_MM);
    const maxX = Math.floor(right / SPATIAL_CELL_SIZE_MM);
    const minY = Math.floor(bottom / SPATIAL_CELL_SIZE_MM);
    const maxY = Math.floor(top / SPATIAL_CELL_SIZE_MM);
    const cellCount = (maxX - minX + 1) * (maxY - minY + 1);

    const candidates = new Set<BrickId>();
    if (Number.isFinite(cellCount) && cellCount <= Math.max(64, this.#cells.size * 4)) {
      for (let cy = minY; cy <= maxY; cy++) {
        for (let cx = minX; cx <= maxX; cx++) {
          const bucket = this.#cells.get(cellKey(cx, cy));
          if (bucket !== undefined) for (const id of bucket) candidates.add(id);
        }
      }
    } else {
      // A very large or sparse query is faster as one brick scan than as
      // millions of empty hash-cell lookups (clause 5.2).
      for (const id of this.#byId.keys()) candidates.add(id);
    }

    const hits: PlacedBrick[] = [];
    for (const id of candidates) {
      const brick = this.#byId.get(id);
      if (brick === undefined) continue;
      if (
        brick.right >= left - EPSILON &&
        brick.x <= right + EPSILON &&
        brick.top >= bottom - EPSILON &&
        brick.y <= top + EPSILON
      ) {
        hits.push(brick);
      }
    }
    return hits;
  }
}
